import React, { useRef, useCallback, useEffect, useState } from "react";
import AudioFile from "../assets/kastro_kyllinis_1.mp3";
import PauseIcon from "../assets/pause2.svg";
import PlayIcon from "../assets/play2.svg";
import { useWavesurfer } from "@wavesurfer/react";
import styled from "styled-components";
import Text from "./Text";
import Union from "../assets/Union.png";

const AudioWrapper = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 20px;
  background-color: var(--bar-bg);
  border-radius: 8px;
  padding-right: 20px;

  width: 100%;
  flex-wrap: nowrap; /* ✅ Keep everything on one line */

  @media (max-width: 1024px) {
    max-width: 260px;
  }
`;



const ControlIcon = styled.img`
  width: 35px;
  height: 35px;
  margin: 10px;
`;

const ControlButton = styled.button`
  border: none;
  border-radius: 50%;
  display: flex;

  cursor: pointer;

  background-color: var(--bar-bg);

  &:focus,
  &:active {
    outline: none;
    border: none;
  }
`;

const CountdownWrapper = styled.div`
  min-width: 65px;
  flex-shrink: 0;         /* ✅ Don't let it shrink too small */
  text-align: right;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;    /* ✅ Prevent line break */
`;



const PopupWrapper = styled.div`
  position: absolute;
  top: -30px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const PopupImage = styled.img`
  width: 35px;
  height: 45px;
`;

const PopupText = styled.div`
  position: absolute;
  top: 11px;
  font-family: "Alegreya";
  font-size: 18px;
  font-weight: 700;
  color: white;
`;

const WaveformContainer = styled.div`
  width: 100%;
  max-width: 400px;

  @media (max-width: 1024px) {
    max-width: 260px;
  }

  div {
    width: 100% !important; /* <-- this forces the wave inside to match */
    overflow: hidden;
  }
`;




const Audio = ({ sendDataToParent, selectedDifficulty }) => {
  const containerRef = useRef(null);
  const [duration, setDuration] = useState(0);
  const [currentSeconds, setCurrentSeconds] = useState(0);
  const [timestamps, setTimestamps] = useState([]);
  const timestampsRef = useRef([]);
  const [timestampsPlay, setTimestampsPlay] = useState();
  const [timestampsPause, setTimestampsPause] = useState();
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const wavesurferRef = useRef(null); // Store wavesurfer instance
  const [pointReduction, setPointReduction] = useState(null); // will be either -1 or -2
  const [audioStep, setAudioStep] = useState(sessionStorage.getItem("castle_audio_step"));
  
  



  const newTimestamps = [];

  const getWaveWidth = () => {
  const screenWidth = window.innerWidth;
  
  if (screenWidth <= 1024) return 100;
  return 400;
};

const [waveWidth, setWaveWidth] = useState(getWaveWidth());

useEffect(() => {
  const handleResize = () => setWaveWidth(getWaveWidth());
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, []);

const { wavesurfer, isPlaying } = useWavesurfer({
  container: containerRef,
  height: 30,
  width: waveWidth,
  waveColor: "#FFFFFF",
  progressColor: "#F3DE8A",
  cursorColor: "#F3DE8A",
  cursorWidth: 2,
  normalize: true,
  barWidth: 3,
  barRadius: 3,
  barGap: 3,
  hideScrollbar: true,
  dragToSeek: false,
  interact: true,
  autoScroll: true,
  url: AudioFile,
});

  

  const onPlayPause = useCallback(() => {
    wavesurfer && wavesurfer.playPause();

    //get audio duration after pressing play/pause and round the numbers
    const roundedDuration = Math.floor(wavesurfer.media.duration);
    const roundedCurrentTime = Math.round(wavesurfer.media.currentTime);
    setDuration(roundedDuration);
    setCurrentSeconds(roundedCurrentTime);
  }, [wavesurfer]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? "0" + secs : secs}`;
  };
  

  useEffect(() => {
    const handleStorageChange = () => {
      setAudioStep(sessionStorage.getItem("castle_audio_step"));
    };
  
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  useEffect(() => {
    if (!selectedDifficulty || !audioStep) return;
  
    const audioPercent =
      parseFloat(sessionStorage.getItem(audioStep === "2" ? "audio_2_listened_percent" : "audio_listened_percent")) || 0;
  
    const basePoints = selectedDifficulty === "easy" ? 3 : 6;
    let points = basePoints;
  
    if (audioPercent >= 66) {
      points = selectedDifficulty === "easy" ? 1 : 2;
    } else if (audioPercent >= 33) {
      points = selectedDifficulty === "easy" ? 2 : 4;
    }
  
    const reduction = basePoints - points;
    setPointReduction(reduction > 0 ? `-${reduction}` : null);
  }, [selectedDifficulty, audioStep]);
  

  

  useEffect(() => {
    if (!wavesurfer) return;
  
    const step = sessionStorage.getItem("castle_audio_step");
    const audioKey = step === "2" ? "castle_audio_2" : "castle_audio_1";
    const audioPath = sessionStorage.getItem(audioKey);
  
    console.log("🎙️ Audio step:", step);
    console.log("🎙️ Audio key:", audioKey);
    console.log("🎙️ Retrieved path:", audioPath);
  
    const finalPath = audioPath ? `/${decodeURIComponent(audioPath)}` : null;
  
    if (finalPath) {
      const fullUrl = `${window.location.origin}${finalPath}`;
      console.log("🎧 Loading audio:", fullUrl);
      wavesurfer.load(fullUrl);
      wavesurfer.once("ready", () => {
        setDuration(Math.floor(wavesurfer.getDuration()));
      });
      
    } else {
      console.warn("⚠️ No audio path found in sessionStorage!");
    }
  }, [wavesurfer]);
  
  
  

  useEffect(() => {
    let interval;
    if (isPlaying && wavesurfer) {
      interval = setInterval(() => {
        setCurrentSeconds(Math.floor(wavesurfer.getCurrentTime()));
      }, 500); // Update every half second
    }
  
    return () => {
      clearInterval(interval);
    };
  }, [isPlaying, wavesurfer]);
  

  useEffect(() => {
    if (!wavesurfer || wavesurferRef.current) return; // Wait until wavesurfer is initialized

    var timestampPlayTemp;
    var timestampPauseTemp;
    var timestampMoved;
    var audioPlaying;

    wavesurferRef.current = wavesurfer;
  
    const handlePause = () => {
      setIsAudioPlaying(false);
      wavesurfer.toggleInteraction(true);
    
      const currentPause = parseInt(wavesurfer.getCurrentTime());
      const currentPlay = timestampPlayTemp;
    
      const newEntry = [currentPlay, currentPause];
      const updatedTimestamps = [...timestamps, newEntry];
      setTimestamps(updatedTimestamps);
      timestampsRef.current = updatedTimestamps;

    
      const merged = [];
      const sorted = [...timestamps].sort((a, b) => a[0] - b[0]);
    
      for (let i = 0; i < sorted.length; i++) {
        const [start, end] = sorted[i];
        if (merged.length === 0 || merged[merged.length - 1][1] < start) {
          merged.push([start, end]);
        } else {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
        }
      }
    
      const totalListenedSeconds = merged.reduce((sum, [start, end]) => sum + (end - start), 0);
      const fullDuration = Math.floor(wavesurfer.getDuration());
      const listenedPercent = ((totalListenedSeconds / fullDuration) * 100).toFixed(2);
    
      console.log("🧩 Merged intervals:", merged);
      console.log("🎧 Total unique listened seconds:", totalListenedSeconds);
      console.log("🎯 Final Percent listened:", listenedPercent, "%");
      console.log("🎵 Full duration:", fullDuration, "seconds");
    
      if (sessionStorage.getItem("castle_audio_step") === "2") {
        sessionStorage.setItem("audio_2_listened_percent", listenedPercent);
      } else {
        sessionStorage.setItem("audio_listened_percent", listenedPercent);
      }
    
      // ✅ Realtime popup logic here
      if (selectedDifficulty) {
        let basePoints = selectedDifficulty === "easy" ? 3 : 6;
        let currentPoints = basePoints;
    
        if (listenedPercent >= 66) {
          currentPoints = selectedDifficulty === "easy" ? 1 : 2;
        } else if (listenedPercent >= 33) {
          currentPoints = selectedDifficulty === "easy" ? 2 : 4;
        }
    
        const reduction = basePoints - currentPoints;
        setPointReduction(reduction > 0 ? `-${reduction}` : null);
      }
    
      sendDataToParent([currentPlay, currentPause]);
    };
    
    const calculateAndStoreListenedPercent = (tempTimestamps, wavesurfer, selectedDifficulty, setPointReduction) => {
      const merged = [];
      const sorted = tempTimestamps.sort((a, b) => a[0] - b[0]);
    
      for (let i = 0; i < sorted.length; i++) {
        const [start, end] = sorted[i];
        if (merged.length === 0 || merged[merged.length - 1][1] < start) {
          merged.push([start, end]);
        } else {
          merged[merged.length - 1][1] = Math.max(merged[merged.length - 1][1], end);
        }
      }
    
      const totalListenedSeconds = merged.reduce((sum, [start, end]) => sum + (end - start), 0);
      const fullDuration = Math.floor(wavesurfer.getDuration());
      const listenedPercent = ((totalListenedSeconds / fullDuration) * 100).toFixed(2);
    
      // ✅ Save to sessionStorage
      const audioStep = sessionStorage.getItem("castle_audio_step");
      if (audioStep === "2") {
        sessionStorage.setItem("audio_2_listened_percent", listenedPercent);
      } else {
        sessionStorage.setItem("audio_listened_percent", listenedPercent);
      }
    
      // ✅ Live popup & point reduction
      if (selectedDifficulty) {
        const basePoints = selectedDifficulty === "easy" ? 3 : 6;
        let currentPoints = basePoints;
    
        if (listenedPercent >= 66) {
          currentPoints = selectedDifficulty === "easy" ? 1 : 2;
        } else if (listenedPercent >= 33) {
          currentPoints = selectedDifficulty === "easy" ? 2 : 4;
        }
    
        const reduction = basePoints - currentPoints;
        setPointReduction(reduction > 0 ? `-${reduction}` : null);
      }
    };
    
    // 🟢 HANDLE PLAY
    const handlePlay = () => {
      setIsAudioPlaying(true);
      wavesurfer.toggleInteraction(false);
    
      audioPlaying = true;
      timestampPlayTemp = parseInt(wavesurfer.getCurrentTime());
    };
    
    
    
    // 🟡 HANDLE PLAYING
    const handlePlaying = () => {
      if (!audioPlaying) return;
    
      const currentTime = Math.floor(wavesurfer.getCurrentTime());
    
      let updatedTimestamps = [...timestampsRef.current];
      const lastTimestamp = updatedTimestamps[updatedTimestamps.length - 1];
    
      if (
        updatedTimestamps.length === 0 ||
        (lastTimestamp && currentTime > lastTimestamp[1])
      ) {
        // Update the last range or push new one
        if (lastTimestamp && lastTimestamp[1] === lastTimestamp[0]) {
          lastTimestamp[1] = currentTime;
        } else {
          updatedTimestamps.push([currentTime - 1, currentTime]);
        }
    
        timestampsRef.current = updatedTimestamps;
        setTimestamps(updatedTimestamps);
      }
    
      calculateAndStoreListenedPercent(updatedTimestamps, wavesurfer, selectedDifficulty, setPointReduction);
    };
    
    
    
    

    const handleMove = () => {
      if (audioPlaying) {
        console.log(wavesurfer.getCurrentTime());
        console.log("fired");
        wavesurfer.pause();
      }
    };

    // Cleanup existing event listeners to prevent stacking
    wavesurfer.un("play", handlePlay);
    wavesurfer.un("pause", handlePause);
    wavesurfer.un("timeupdate", handlePlaying);
    wavesurfer.un("seeking", handleMove);

    // Attach event listeners only once
    wavesurfer.on("play", handlePlay);
    wavesurfer.on("pause", handlePause);
    wavesurfer.on("timeupdate", handlePlaying);
    wavesurfer.on("seeking", handleMove);

    return () => {
      wavesurfer.un("play", handlePlay);
      wavesurfer.un("pause", handlePause);
      wavesurfer.un("timeupdate", handlePlaying);
      wavesurfer.un("seeking", handleMove);
    };
  }, [wavesurfer]); // Only runs when wavesurfer is initialized

  return (
    <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
  {pointReduction && (
    <PopupWrapper>
      <PopupImage src={Union} />
      <PopupText>{pointReduction}</PopupText>
    </PopupWrapper>
  )}

  <AudioWrapper>
    <ControlButton onClick={onPlayPause}>
      {isPlaying ? (
        <ControlIcon src={PauseIcon} />
      ) : (
        <ControlIcon src={PlayIcon} />
      )}
    </ControlButton>

    <WaveformContainer>
    <div ref={containerRef} />
  </WaveformContainer>
    <CountdownWrapper>
    <Text
  color="white"
  fontFamily="Manrope"
  fontWeight="500"
  fontSize="16px"
  lineheight="21px"
>
  {formatTime(currentSeconds)} / {formatTime(duration)}
</Text>

    </CountdownWrapper>
  </AudioWrapper>
</div>

  );
};

export default Audio;
